import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import CircularProgress from "@mui/material/CircularProgress";
import { UserContext } from "../../../../../../App";
import { useSnackbar } from "../../../hooks/SnackbarProvider";
import {
  loginTimeline,
  getPosUserRole,
  fetchOrganisations,
  getUserRolesById,
} from "../../../../../../config/Services";
import { Account } from "../Account";
import "./index.css";

/* ══════════════════════════════════════════════════════════
   Helpers
   ══════════════════════════════════════════════════════════ */
function decodeJwtExpiry(token) {
  if (!token) return null;
  try {
    const b64 = token.split(".")[1];
    if (!b64) return null;
    const { exp } = JSON.parse(atob(b64.replace(/-/g, "+").replace(/_/g, "/")));
    return exp ? new Date(exp * 1000) : null;
  } catch { return null; }
}

function fmtRelative(date) {
  if (!date) return "—";
  const diffMin = Math.round((date - Date.now()) / 60000);
  if (diffMin < -1)  return `expired ${Math.abs(diffMin)}min ago`;
  if (diffMin <= 1)  return "expiring now";
  if (diffMin < 60)  return `in ${diffMin}min`;
  const h = Math.floor(diffMin / 60);
  const m = diffMin % 60;
  return m === 0 ? `in ${h}h` : `in ${h}h ${m}min`;
}

function parseAnyDate(str) {
  if (!str) return null;
  /* Try ISO first */
  let d = new Date(str);
  if (!isNaN(d)) return d;
  /* API returns locale strings like "7/30/26, 7:39:50 PM" or "8/4/26, 11:53:21 AM" */
  const m = String(str).match(
    /^(\d{1,2})\/(\d{1,2})\/(\d{2,4}),?\s+(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM)$/i
  );
  if (!m) return null;
  let [, mo, dy, yr, hr, min, ap] = m;
  yr = parseInt(yr); if (yr < 100) yr += 2000;
  hr = parseInt(hr);
  if (ap.toUpperCase() === "PM" && hr !== 12) hr += 12;
  if (ap.toUpperCase() === "AM" && hr === 12) hr = 0;
  d = new Date(yr, parseInt(mo) - 1, parseInt(dy), hr, parseInt(min));
  return isNaN(d) ? null : d;
}

function fmtDateTime(isoStr) {
  if (!isoStr) return "—";
  try {
    const d = parseAnyDate(isoStr);
    if (!d) return String(isoStr); /* show raw if unparseable */
    const day = String(d.getDate()).padStart(2, "0");
    const mon = d.toLocaleString("en-US", { month: "short" });
    const year = d.getFullYear();
    const hh = d.getHours();
    const mm = String(d.getMinutes()).padStart(2, "0");
    const ampm = hh >= 12 ? "PM" : "AM";
    const h12 = String(hh % 12 || 12).padStart(2, "0");
    return `${day} ${mon} ${year}, ${h12}:${mm} ${ampm}`;
  } catch { return "—"; }
}

function fmtRelativeFromAny(str) {
  const d = parseAnyDate(str);
  return d ? fmtRelative(d) : "—";
}

function fmtApiDate(d) {
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const yy = String(d.getFullYear()).slice(-2);
  return `${mm}/${dd}/${yy}`;
}

/* ══════════════════════════════════════════════════════════
   Static data
   ══════════════════════════════════════════════════════════ */
/* Role label + colour */
const ROLE_STYLES = {
  LOCATION_ADMIN:  { bg: "#EEF3FF", color: "#1662E8", label: "Location Admin" },
  LOCATION_VIEWER: { bg: "#F0FDF4", color: "#16A34A", label: "Location Viewer" },
  HRMS_ADMIN:      { bg: "#F5F3FF", color: "#7C3AED", label: "HR Admin" },
  HRMS_VIEWER:     { bg: "#FFF7ED", color: "#D97706", label: "HR Viewer" },
  POS_MANAGER:     { bg: "#EEF3FF", color: "#1662E8", label: "POS Manager" },
  POS_USER:        { bg: "#F0F9FF", color: "#0284C7", label: "POS User" },
  MANAGER:         { bg: "#EEF3FF", color: "#1662E8", label: "Manager" },
  CASHIER:         { bg: "#F0FDF4", color: "#16A34A", label: "Cashier" },
  ADMIN:           { bg: "#F5F3FF", color: "#7C3AED", label: "Admin" },
  OWNER:           { bg: "#FFF7ED", color: "#D97706", label: "Owner" },
  USER:            { bg: "#F3F4F6", color: "#374151", label: "User" },
  STAFF:           { bg: "#F0F9FF", color: "#0284C7", label: "Staff" },
};

function roleStyle(slug) {
  if (!slug) return { bg: "#F3F4F6", color: "#374151", label: slug };
  const s = ROLE_STYLES[slug.toUpperCase()];
  if (s) return s;
  /* prettify unknown slugs: SOME_ROLE → Some Role */
  const label = slug.split("_").map((w) =>
    w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()
  ).join(" ");
  return { bg: "#F3F4F6", color: "#374151", label };
}

/* ══════════════════════════════════════════════════════════
   Inline SVG icon helper
   ══════════════════════════════════════════════════════════ */
const ICON_PATHS = {
  "arrow-left":       "M19 12H5m7-7-7 7 7 7",
  "shield-checkmark": "M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z",
  "clock":            "M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z",
  "log-in":           "M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1",
  "refresh":          "M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15",
  "key":              "M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z",
  "lock-closed":      "M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z",
  "bell":             "M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9",
  "sync":             "M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15",
  "alert-circle":     "M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
  "chevron-down":     "M19 9l-7 7-7-7",
  "chevron-right":    "M9 5l7 7-7 7",
  "chevron-up":       "M5 15l7-7 7 7",
};

function Ico({ name, size = 16, color = "currentColor" }) {
  const d = ICON_PATHS[name];
  if (!d) return null;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true" style={{ flexShrink: 0 }}>
      <path d={d} />
    </svg>
  );
}

/* ══════════════════════════════════════════════════════════
   Main component
   ══════════════════════════════════════════════════════════ */
export default function RolesPermissions() {
  const navigate = useNavigate();
  const { setActiveTabComp, setActiveIndex } = useContext(UserContext);
  const showSnackbar = useSnackbar();

  /* 60-second tick keeps JWT countdowns live */
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 60000);
    return () => clearInterval(id);
  }, []);

  /* ── Roles state ── */
  const [roles, setRoles]           = useState([]);
  const [rolesLoading, setRolesLoading] = useState(true);
  const [rolesError, setRolesError] = useState(null);

  /* ── Activity timeline ── */
  const [tlExpanded, setTlExpanded] = useState(false);
  const [tlLoaded,   setTlLoaded]   = useState(false);
  const [tlLoading,  setTlLoading]  = useState(false);
  const [tlData,     setTlData]     = useState([]);
  const [tlError,    setTlError]    = useState(null);

  /* ── Read localStorage ── */
  const at = localStorage.getItem("access_token");
  const rt = localStorage.getItem("refresh_token");
  const sessionStartedAt  = localStorage.getItem("sessionStartedAt");
  const lastTokenRefresh  = localStorage.getItem("lastTokenRefresh");
  const retailUser        = JSON.parse(localStorage.getItem("retailUserDetails") || "{}");
  const userUidx          = retailUser?.uidx || null;
  const fcmToken          = localStorage.getItem("fcmToken");
  const fcmStatus         = localStorage.getItem("fcmStatus");
  const fcmError          = localStorage.getItem("fcmError");
  const fcmTokenUpdatedAt = localStorage.getItem("fcmTokenUpdatedAt");
  const locationName      = localStorage.getItem("LocName") || "—";
  const orgName           = localStorage.getItem("orgName") || "—";

  /* ── Fetch roles on mount ── */
  useEffect(() => {
    if (!userUidx) {
      setRolesError("No user ID found. Please log in again.");
      setRolesLoading(false);
      return;
    }

    /* Check every localStorage key that any login/location path might have written.
       Priority:
         1. user_roles      — written by LocationModal on every org-fetch (most reliable)
         2. retailUserDetails.roles — written by PIN LoginPage.js
         3. userRoles       — written by Account.js getUserRolesById chain
    */
    const tryParse = (key) => {
      try { return JSON.parse(localStorage.getItem(key) || "null"); } catch { return null; }
    };

    const fromLocationModal  = tryParse("user_roles");          // array of slugs
    const fromRetailDetails  = retailUser?.roles;               // from PIN login
    const fromAccountScreen  = tryParse("userRoles");           // from Account.js

    const cached =
      (Array.isArray(fromLocationModal) && fromLocationModal?.length > 0 ? fromLocationModal : null) ||
      (Array.isArray(fromRetailDetails) && fromRetailDetails?.length > 0 ? fromRetailDetails : null) ||
      (Array.isArray(fromAccountScreen) && fromAccountScreen?.length > 0 ? fromAccountScreen : null);

    if (cached) {
      setRoles(cached);
      setRolesError(null);
      setRolesLoading(false);
      return;
    }

    /* Nothing in localStorage → fetch live */
    /* Fallback 1: getPosUserRole — returns POS-context roles */
    getPosUserRole({ uidx: userUidx })
      .then((res) => {
        const raw = res?.data?.data;
        const list = Array.isArray(raw) ? raw : [];
        if (list?.length > 0) {
          setRoles(list);
          setRolesError(null);
          return;
        }
        /* Fallback 2: getUserRolesById via org */
        return fetchOrganisations().then((orgRes) => {
          const orgIdList = orgRes?.data?.data?.orgIdList?.RETAIL;
          return getUserRolesById(userUidx, orgIdList).then((r) => {
            const arr = r?.data?.data?.roles || [];
            setRoles(arr);
            setRolesError(arr?.length === 0 ? "No roles found for this account" : null);
          });
        });
      })
      .catch(() => {
        /* Last resort: cached userRoles from Account screen */
        const cached = JSON.parse(localStorage.getItem("userRoles") || "[]");
        if (cached?.length > 0) {
          setRoles(cached);
          setRolesError(null);
        } else {
          setRolesError("Could not load roles. Check your connection.");
        }
      })
      .finally(() => setRolesLoading(false));
  }, [userUidx]);

  /* ── JWT expiry ── */
  const atExpiry    = decodeJwtExpiry(at);
  const rtExpiry    = decodeJwtExpiry(rt);
  const nextRefresh = lastTokenRefresh
    ? new Date(new Date(lastTokenRefresh).getTime() + 30 * 60000)
    : null;

  const atSoon = atExpiry && (atExpiry - Date.now()) < 5  * 60000;
  const rtSoon = rtExpiry && (rtExpiry - Date.now()) < 60 * 60000;
  const atColor  = atSoon ? "#E11D48" : "#0891B2";
  const atIconBg = atSoon ? "#FFF1F2" : "#ECFEFF";
  const rtColor  = rtSoon ? "#E11D48" : "#7C3AED";
  const rtIconBg = rtSoon ? "#FFF1F2" : "#F5F3FF";

  /* ── Back ── */
  const handleBack = () => {
    navigate(-1);
  };

  /* ── Timeline ── */
  const handleTlToggle = () => {
    const next = !tlExpanded;
    setTlExpanded(next);
    if (next && !tlLoaded && !tlLoading) fetchTimeline();
  };

  const fetchTimeline = () => {
    if (!userUidx) { setTlError("No user ID available"); setTlLoaded(true); return; }
    setTlLoading(true);
    const now  = new Date();
    const past = new Date(now - 90 * 86400000);
    loginTimeline({ uidx: userUidx, startDate: fmtApiDate(past), endDate: fmtApiDate(now) })
      .then((res) => {
        const items = Array.isArray(res?.data?.data) ? res.data.data : [];
        setTlData(
          [...items]
            .sort((a, b) => new Date(b?.issue_date) - new Date(a?.issue_date))
            .slice(0, 5)
        );
        setTlError(null);
        setTlLoaded(true);
      })
      .catch(() => { setTlError("Could not load activity history"); setTlLoaded(true); })
      .finally(() => setTlLoading(false));
  };

  /* ── FCM row ── */
  let fcmValue, fcmSub, fcmIcon, fcmColor, fcmIconBg;
  if (fcmStatus === "registering") {
    fcmValue = "Refreshing…"; fcmSub = null;
    fcmIcon = "sync"; fcmColor = "#D97706"; fcmIconBg = "#FFFBEB";
  } else if (fcmStatus === "error") {
    fcmValue = "Update failed"; fcmSub = fcmError || "Unable to reach the server";
    fcmIcon = "alert-circle"; fcmColor = "#E11D48"; fcmIconBg = "#FFF1F2";
  } else if (fcmToken) {
    fcmValue = "Token updated"; fcmSub = fmtDateTime(fcmTokenUpdatedAt);
    fcmIcon = "bell"; fcmColor = "#16A34A"; fcmIconBg = "#F0FDF4";
  } else {
    fcmValue = "Not registered"; fcmSub = "Registers after location select";
    fcmIcon = "bell"; fcmColor = "#D97706"; fcmIconBg = "#FFFBEB";
  }

  /* ── Security rows ── */
  const secRows = [
    {
      label: "Session Started", value: fmtDateTime(sessionStartedAt),
      icon: "log-in", color: "#1662E8", iconBg: "#EEF3FF",
    },
    {
      label: "Last Token Refresh", value: fmtDateTime(lastTokenRefresh),
      icon: "refresh", color: "#16A34A", iconBg: "#F0FDF4",
    },
    {
      label: "Next Auto-Refresh",
      value: nextRefresh ? fmtRelative(nextRefresh) : "—",
      sub: nextRefresh ? fmtDateTime(nextRefresh.toISOString()) : null,
      icon: "clock", color: "#D97706", iconBg: "#FFFBEB",
    },
    {
      label: "Access Token Expiry",
      value: atExpiry ? fmtRelative(atExpiry) : at ? "—" : "Not available",
      sub: atExpiry ? fmtDateTime(atExpiry.toISOString()) : null,
      icon: "key", color: atColor, iconBg: atIconBg,
    },
    {
      label: "Refresh Token Expiry",
      value: rtExpiry ? fmtRelative(rtExpiry) : rt ? "—" : "Not stored",
      sub: rtExpiry ? fmtDateTime(rtExpiry.toISOString()) : null,
      icon: "lock-closed", color: rtColor, iconBg: rtIconBg,
    },
    {
      label: "Refresh Strategy", value: "Every 30 min + on 401",
      icon: "shield-checkmark", color: "#7C3AED", iconBg: "#F5F3FF",
    },
    {
      label: "Push Notification Token", value: fcmValue, sub: fcmSub,
      icon: fcmIcon, color: fcmColor, iconBg: fcmIconBg,
      action: fcmStatus !== "registering"
        ? () => showSnackbar("Push notification registration is coming soon", "info")
        : null,
    },
  ];

  /* ════════════════════════════════════════════════════════
     Render
     ════════════════════════════════════════════════════════ */
  return (
    <div className="rp-container">
      {/* ── Header ── */}
      <div className="rp-header">
        <button className="rp-back" onClick={handleBack} aria-label="Back to Profile">
          <Ico name="arrow-left" size={15} />
        </button>
        <div>
          <p className="rp-header-title">Roles &amp; Permissions</p>
          <p className="rp-header-sub">{locationName} · {orgName}</p>
        </div>
      </div>

      {/* ── Body ── */}
      <div className="rp-body">

        {/* ① Assigned Roles */}
        <div className="rp-card">
          <div className="rp-card-inner-header">
            <div className="rp-card-icon" style={{ background: "#EEF3FF" }}>
              <Ico name="shield-checkmark" size={17} color="#1662E8" />
            </div>
            <div>
              <p className="rp-card-title">Assigned Roles</p>
              <p className="rp-card-sub">{locationName} · {orgName}</p>
            </div>
          </div>

          <div className="rp-divider" />

          <div className="rp-roles-pills">
            {rolesLoading && (
              <span className="rp-roles-loading">
                <CircularProgress size={13} sx={{ color: "#2a71ff" }} />
                <span>Loading roles…</span>
              </span>
            )}
            {!rolesLoading && rolesError && (
              <span className="rp-no-roles rp-roles-err">
                <Ico name="alert-circle" size={13} color="#ef4444" />
                {rolesError}
              </span>
            )}
            {!rolesLoading && !rolesError && roles?.length === 0 && (
              <span className="rp-no-roles">No roles assigned for this account</span>
            )}
            {!rolesLoading && roles?.map((slug, i) => {
              const s = roleStyle(slug);
              return (
                <span key={i} className="rp-role-pill" style={{ background: s?.bg, color: s?.color }}>
                  <Ico name="shield-checkmark" size={12} color={s?.color} />
                  {s?.label}
                </span>
              );
            })}
          </div>
        </div>

        {/* ② User Activity Timeline */}
        <div className="rp-card">
          <button className="rp-accordion-btn" onClick={handleTlToggle}
            aria-expanded={tlExpanded} aria-controls="rp-tl-body">
            <div className="rp-accordion-left">
              <div className="rp-card-icon" style={{ background: "#EEF3FF" }}>
                <Ico name="clock" size={17} color="#1662E8" />
              </div>
              <div>
                <p className="rp-card-title">User Activity Timeline</p>
                <p className="rp-card-sub">Last 90 days · up to 5 entries</p>
              </div>
            </div>
            <Ico name={tlExpanded ? "chevron-up" : "chevron-down"} size={16} color="#64748b" />
          </button>

          {tlExpanded && (
            <div className="rp-accordion-body" id="rp-tl-body">
              {tlLoading && (
                <div className="rp-tl-loading">
                  <CircularProgress size={14} sx={{ color: "#2a71ff" }} />
                  <span>Loading activity…</span>
                </div>
              )}
              {!tlLoading && tlError && (
                <div className="rp-tl-error">
                  <Ico name="alert-circle" size={14} color="#ef4444" />
                  {tlError}
                </div>
              )}
              {!tlLoading && !tlError && tlData?.length === 0 && (
                <div className="rp-tl-empty">No activity found in the last 90 days.</div>
              )}
              {!tlLoading && !tlError && tlData?.length > 0 && (
                <div className="rp-timeline">
                  {tlData?.map((item, i) => (
                    <div className="rp-tl-item" key={item?.id ?? i}>
                      <div className="rp-tl-connector">
                        <div className="rp-tl-dot" />
                        {i < tlData?.length - 1 && <div className="rp-tl-line" />}
                      </div>
                      <div className="rp-tl-content">
                        <div className="rp-tl-row1">
                          {item?.type && <span className="rp-tl-badge">{item?.type}</span>}
                          {item?.platform && <span className="rp-tl-platform">{item?.platform}</span>}
                        </div>
                        <p className="rp-tl-date">{fmtDateTime(item?.issue_date)}</p>
                        {(item?.at_expiry || item?.rt_expiry) && (
                          <p className="rp-tl-expiry">
                            {item?.at_expiry && <>AT expiry {fmtDateTime(item?.at_expiry)}</>}
                            {item?.at_expiry && item?.rt_expiry && <> · </>}
                            {item?.rt_expiry && <>RT expiry {fmtDateTime(item?.rt_expiry)}</>}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* ③ Session & Security */}
        <div>
          <p className="rp-section-label">Session &amp; Security</p>
          <div className="rp-card">
            {secRows?.map((row) => (
              <div className="rp-sec-row" key={row?.label}>
                <div className="rp-sec-icon" style={{ background: row?.iconBg, color: row?.color }}>
                  <Ico name={row?.icon} size={15} color={row?.color} />
                </div>
                <div className="rp-sec-content">
                  <p className="rp-sec-label">{row?.label}</p>
                  <p className="rp-sec-value">{row?.value}</p>
                  {row?.sub && <p className="rp-sec-sub">{row?.sub}</p>}
                </div>
                {row?.action && (
                  <button className="rp-sec-action" onClick={row?.action}
                    aria-label="More info">
                    <Ico name="refresh" size={12} color="#64748b" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
}
